import { useEffect, useRef, useState } from "react";
import { ServerWithName } from "@/hooks/use-app-state";
import { cn } from "@/lib/utils";
import { AddServerModal } from "./connection/AddServerModal";
import { ServerFormData } from "@/shared/types.js";
import { Check, ChevronLeft, ChevronRight, RefreshCw, X } from "lucide-react";
import { track } from "@/lib/analytics";
import { HOSTED_MODE } from "@/lib/config";
import { isOAuthDebuggerHeaderServer } from "@/lib/debugger-header-servers";

const HOSTED_HTTPS_REQUIRED_HINT =
  "Hosted mode requires HTTPS server URLs. Edit this server to use https://.";

function isHostedInsecureHttpServer(server: ServerWithName): boolean {
  if (!HOSTED_MODE || !("url" in server.config) || !server.config.url) {
    return false;
  }

  try {
    return new URL(server.config.url.toString()).protocol === "http:";
  } catch {
    return false;
  }
}

export interface ActiveServerSelectorProps {
  serverConfigs: Record<string, ServerWithName>;
  selectedServer: string;
  selectedMultipleServers: string[];
  isMultiSelectEnabled: boolean;
  onServerChange: (server: string) => void;
  onMultiServerToggle: (server: string) => void;
  /**
   * Bulk-replace the multi-server selection. Wired in for the Playground
   * tab's host snapshot — picking a named host should toggle on the host's
   * required + optional servers in one shot, not per-server. Optional on
   * the shared interface so single-select callers don't need it.
   */
  onSelectMultipleServers?: (serverNames: string[]) => void;
  onConnect: (formData: ServerFormData) => void;
  /**
   * Override the "Add Server" click. When provided, the button calls this
   * instead of opening the generic Add Server modal — used by the XAA / OAuth
   * debuggers to open their own purpose-built "configure server" modals.
   */
  onAddServerRequested?: () => void;
  onReconnect?: (serverName: string) => Promise<void>;
  /** Disconnect a connected server (Playground toggle off = unplug). */
  onDisconnect?: (serverName: string) => void;
  /**
   * Hide a server from THIS header only (OAuth / XAA debugger). View-only: the
   * server config, tokens, and Convex row are untouched — it's dropped from this
   * tab's chip strip until un-hidden. When provided, each chip renders an "x".
   */
  onHideServer?: (serverName: string) => void;
  /** Server names hidden from this header; filtered out of the rendered list. */
  hiddenServers?: Set<string>;
  showOnlyOAuthServers?: boolean; // Only show servers that use OAuth
  showOnlyServersWithViews?: boolean; // Only show servers that have saved views
  /** Auto-select when the current selection is hidden by filters. `true`
   * replaces an invalid selection with the most recently connected eligible
   * server. `"when-empty"` only fills a blank selection ("none") and never
   * replaces an existing one — the debugger tabs use this so their target
   * (which live auth requests are fired at) can't change without an explicit
   * click. */
  autoSelectFilteredServer?: boolean | "when-empty";
  serversWithViews?: Set<string>; // Set of server names that have saved views
  hasMessages?: boolean; // Reserved for callers that still compute this
  className?: string;
}

/** Props supplied by the shell; `className` is set in PlaygroundMain. */
export type PlaygroundServerSelectorProps = Omit<
  ActiveServerSelectorProps,
  "hasMessages" | "className"
>;

function getStatusColor(status: string): string {
  switch (status) {
    case "connected":
      return "bg-green-500 dark:bg-green-400";
    case "connecting":
      return "bg-yellow-500 dark:bg-yellow-400 animate-pulse";
    case "failed":
      return "bg-red-500 dark:bg-red-400";
    case "disconnected":
      return "bg-muted-foreground";
    default:
      return "bg-muted-foreground";
  }
}

function getStatusText(status: string): string {
  switch (status) {
    case "connected":
      return "Connected";
    case "connecting":
      return "Connecting...";
    case "failed":
      return "Failed";
    case "disconnected":
      return "Disconnected";
    default:
      return "Unknown";
  }
}

export function ActiveServerSelector({
  serverConfigs,
  selectedServer,
  selectedMultipleServers,
  isMultiSelectEnabled,
  onServerChange,
  onMultiServerToggle,
  onConnect,
  onAddServerRequested,
  onReconnect,
  onHideServer,
  hiddenServers,
  showOnlyOAuthServers = false,
  showOnlyServersWithViews = false,
  autoSelectFilteredServer = true,
  serversWithViews,
  className,
}: ActiveServerSelectorProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasNoServersWithViews =
    showOnlyServersWithViews && (serversWithViews?.size ?? 0) === 0;

  const servers = Object.entries(serverConfigs).filter(([name, server]) => {
    // View-only dismissals from this header (OAuth / XAA debugger x button).
    if (hiddenServers?.has(name)) return false;
    if (showOnlyOAuthServers && !isOAuthDebuggerHeaderServer(server))
      return false;
    if (showOnlyServersWithViews && !serversWithViews?.has(name)) return false;
    return true;
  });

  // Auto-select first available server if current selection is not in the list
  useEffect(() => {
    if (
      !autoSelectFilteredServer ||
      isMultiSelectEnabled ||
      hasNoServersWithViews
    ) {
      return;
    }

    const serverNames = servers.map(([name]) => name);
    const isCurrentSelectionValid = serverNames.includes(selectedServer);
    const hasExplicitSelection =
      Boolean(selectedServer) && selectedServer !== "none";

    // "when-empty" fills a blank selection but never replaces one the user
    // already made — an invalid selection stays put (the tab renders its own
    // "not testable" state) instead of being silently swapped for a server
    // the user never clicked.
    if (autoSelectFilteredServer === "when-empty" && hasExplicitSelection) {
      return;
    }

    if (!isCurrentSelectionValid && servers.length > 0) {
      // Pick the most recently connected server instead of the first by insertion order
      const sorted = [...servers].sort(
        ([, a], [, b]) =>
          new Date(b.lastConnectionTime).getTime() -
          new Date(a.lastConnectionTime).getTime(),
      );
      onServerChange(sorted[0][0]);
    } else if (!isCurrentSelectionValid && selectedServer !== "none") {
      // No available servers and selection is stale — clear it
      onServerChange("none");
    }
  }, [
    servers.length,
    selectedServer,
    isMultiSelectEnabled,
    onServerChange,
    hasNoServersWithViews,
    autoSelectFilteredServer,
  ]);

  const handleServerClick = (name: string) => {
    if (isMultiSelectEnabled) {
      onMultiServerToggle(name);
      return;
    }
    onServerChange(name);
  };

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const updateScrollState = () => {
      setCanScrollLeft(node.scrollLeft > 0);
      setCanScrollRight(
        node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
      );
    };

    updateScrollState();
    node.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      node.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [servers.length]);

  const scroll = (direction: "left" | "right") => {
    const node = scrollRef.current;
    if (!node) return;
    // Scroll by approximately 2 tab widths (200px) for smooth incremental navigation
    const scrollAmount = 200;
    const newScrollLeft =
      direction === "left"
        ? Math.max(0, node.scrollLeft - scrollAmount)
        : Math.min(
            node.scrollWidth - node.clientWidth,
            node.scrollLeft + scrollAmount,
          );
    node.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  if (hasNoServersWithViews) {
    return null;
  }

  return (
    <div className={cn("relative h-full w-full min-w-0", className)}>
      <div
        ref={scrollRef}
        className={cn(
          "w-full h-full min-w-0 overflow-x-auto scrollbar-hidden",
          "flex justify-start",
        )}
      >
        <div className="flex flex-nowrap min-w-fit h-full">
          {servers.map(([name, serverConfig]) => {
            const isSelected = isMultiSelectEnabled
              ? selectedMultipleServers.includes(name)
              : selectedServer === name;
            const isHostedHttpReconnectBlocked =
              isHostedInsecureHttpServer(serverConfig);

            return (
              <button
                key={name}
                onClick={(e) => {
                  // Ignore clicks from the inline action buttons (reconnect /
                  // hide). Using Element to cover SVG elements too.
                  if (
                    (e.target as Element).closest(
                      "[data-reconnect-button],[data-hide-button]",
                    )
                  ) {
                    return;
                  }
                  handleServerClick(name);
                }}
                className={cn(
                  "group relative flex h-full items-center gap-3 px-4 border-r border-border transition-all duration-200 cursor-pointer outline-none focus-visible:bg-accent focus-visible:text-accent-foreground",
                  "hover:bg-accent hover:text-accent-foreground",
                  isSelected
                    ? "bg-muted text-foreground"
                    : "bg-background text-foreground",
                )}
              >
                {isMultiSelectEnabled && (
                  <div
                    className={cn(
                      "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-muted-foreground/30 hover:border-primary/50",
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                  </div>
                )}
                <div
                  className={cn(
                    "w-2 h-2 rounded-full",
                    getStatusColor(serverConfig.connectionStatus),
                  )}
                  title={getStatusText(serverConfig.connectionStatus)}
                />
                <span className="text-sm font-medium truncate max-w-36">
                  {name}
                </span>
                <div className="text-xs opacity-70">
                  {serverConfig.config.command ? "STDIO" : "HTTP"}
                </div>
                {onReconnect && (
                  <div
                    role="button"
                    tabIndex={0}
                    data-reconnect-button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      // Also prevent default to avoid double actions if standard button behavior applies
                      e.preventDefault();
                      if (isHostedHttpReconnectBlocked) {
                        return;
                      }
                      onReconnect(name).catch(() => {});
                    }}
                    className={cn(
                      "ml-auto p-1 rounded-md transition-colors",
                      isHostedHttpReconnectBlocked
                        ? "cursor-not-allowed text-muted-foreground/40"
                        : "hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground",
                    )}
                    title={
                      isHostedHttpReconnectBlocked
                        ? HOSTED_HTTPS_REQUIRED_HINT
                        : "Reconnect"
                    }
                    aria-disabled={isHostedHttpReconnectBlocked}
                  >
                    <RefreshCw className="w-3 h-3" />
                  </div>
                )}
                {onHideServer && (
                  <div
                    role="button"
                    tabIndex={0}
                    data-hide-button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      e.preventDefault();
                      onHideServer(name);
                    }}
                    className={cn(
                      "p-1 rounded-md transition-colors text-muted-foreground",
                      "hover:bg-destructive/10 hover:text-destructive",
                      // Right-align the action cluster when there's no
                      // reconnect button to carry the ml-auto.
                      onReconnect ? "" : "ml-auto",
                    )}
                    title="Hide from this tab"
                    aria-label={`Hide ${name} from this header`}
                  >
                    <X className="w-3 h-3" />
                  </div>
                )}
              </button>
            );
          })}

          {/* Add Server Button */}
          <button
            onClick={() => {
              if (onAddServerRequested) {
                onAddServerRequested();
                return;
              }
              setIsAddModalOpen(true);
            }}
            className={cn(
              "group relative flex h-full items-center gap-3 px-4 border-r border-border transition-all duration-200 cursor-pointer",
              "hover:bg-accent hover:text-accent-foreground",
              "bg-background text-muted-foreground border-dashed",
            )}
          >
            {isMultiSelectEnabled && (
              <div className="w-4 h-4" /> // Spacer for alignment
            )}
            <span className="text-sm font-medium">Add Connector</span>
            <div className="text-xs opacity-70">+</div>
          </button>
        </div>

        <AddServerModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={(formData) => {
            track("connecting_server", {
              location: "active_server_selector",
            });
            onConnect(formData);
          }}
        />

        {canScrollLeft && (
          <button
            className="absolute left-0 top-0 h-full px-3 flex items-center bg-gradient-to-r from-background via-background/95 to-background/40 cursor-pointer"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        {canScrollRight && (
          <button
            className="absolute right-0 top-0 h-full px-3 flex items-center bg-gradient-to-l from-background via-background/95 to-background/40 cursor-pointer"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
